<script lang="ts">
  import type { SvgPreviewProps } from '@athena-lib/SvgPreview/types';
  import LoadingDots from '@athena-lib/Icons/LoadingDots.svelte';
  import { usePromise } from '@athena-lib/usePromise.svelte';
  import { BackgroundOption } from '@athena-lib/BackgroundSelector';
  import { MaskUtils, materialOptions, SVGUtils } from '..';
  import type { LoadedFont, MaterialName } from '../types';
  import {
    StickerPreviewAnimationMode,
    type PreviewBaseProps,
    type WhiteLayerMask,
  } from '../types';
  import { LaminateType, WhiteLayerMaskMode } from '../types';
  import StickerPreview from './StickerPreview.svelte';

  const { parts, productItem }: SvgPreviewProps = $props();
  const patternScale = 0.5;
  const previewProps: PreviewBaseProps = {
    patternScale: patternScale,
    sticker: {
      width: 0,
      height: 0,
      pathWidth: 0,
      pathHeight: 0,
      original: {
        height: 0,
        width: 0,
        originOffset: { x: 0, y: 0 },
        path: '',
        pathHeight: 0,
        pathWidth: 0,
      },
      previewImage: '',
      path: '',
      whiteLayerMasks: [],
    },
    laminate: LaminateType.Glossy,
    material: materialOptions[0],
    maskBackground: WhiteLayerMaskMode.Mix,
  };

  const materialSettings = materialOptions.find(
    (material) => material.type === productItem?.attributes['material'],
  );

  const metallicOpacity = $derived.by(() => {
    return typeof materialSettings?.metallicOpacity === 'function'
      ? materialSettings.metallicOpacity(productItem?.attributes['laminate'] as string)
      : (materialSettings?.metallicOpacity ?? 0);
  });

  const propsPromise = $derived.by(async () => {
    let frontPart = parts.find((part) => part.side === 'front');
    if (!frontPart) {
      return previewProps;
    }

    /* What is going on here. Width is not in frontPart if reorder. */
    if (!frontPart.width) {
      frontPart.width = +(productItem?.attributes['width_mm'] ?? 0);
    }

    if (!frontPart.height) {
      frontPart.height = +(productItem?.attributes['height_mm'] ?? 0);
    }

    const realsvg = SVGUtils.generateSvgStringFromPart(frontPart);
    const scale = SVGUtils.extractScale(frontPart.matrix);
    const cutline = SVGUtils.scaleSVGPath(frontPart.cuttingline, scale);
    const fonts = (await SVGUtils.fetchFontsFromSVG(realsvg)) as LoadedFont[];
    const imageUrl = await SVGUtils.createPng(realsvg, fonts, 1080, 1080);
    const svgBoundingBox = SVGUtils.getBoundingBox(realsvg);
    const whiteLayerMasks = await fetchAndProcessMasks();

    return {
      ...previewProps,
      sticker: {
        path: cutline,
        width: svgBoundingBox?.xMax,
        height: svgBoundingBox?.yMax,
        previewImage: imageUrl,
        whiteLayerMasks: whiteLayerMasks,
      },
      laminate:
        (productItem.attributes['laminate']?.toString() as LaminateType) ?? LaminateType.Uncoated,
      material: {
        ...previewProps.material,
        type: productItem?.attributes['material'] as MaterialName,
        metallicOpacity: metallicOpacity,
        isMetal: materialSettings?.isMetal ?? false,
        laminateSettings: {
          enabled: materialSettings?.laminateSettings.enabled ?? true,
          laminateOpacity: materialSettings?.laminateSettings.laminateOpacity,
        },
      },
    };
  });

  async function fetchAndProcessMasks(): Promise<WhiteLayerMask[]> {
    const opacityMaskID = productItem?.attributes['live_preview_opacity_mask']?.toString();
    const whiteLayerMaskID = productItem?.attributes['live_preview_white_layer_mask']?.toString();

    if (!opacityMaskID || !whiteLayerMaskID) {
      return [];
    }
    const params = new URLSearchParams({
      opacityLayer: opacityMaskID || '',
      whiteLayer: whiteLayerMaskID || '',
    });

    try {
      const maskfilesres = await fetch(`/editor/get_masks?${params.toString()}`);
      const masks: WhiteLayerMask[] = (await maskfilesres.json()) as WhiteLayerMask[];
      return masks.length > 0 ? await MaskUtils.prepareCombinedMasks(masks) : [];
    } catch (error) {
      console.log('Failed to load masks:', error);
      return [];
    }
  }
  const awaitedPromise = usePromise(() => {
    return propsPromise;
  });
</script>

{#if awaitedPromise.type == 'loading'}
  <LoadingDots sizeClass="h-12 w-12"></LoadingDots>
{/if}

<StickerPreview
  className="p-2"
  visible={awaitedPromise.type == 'resolved'}
  staticUrl="https://d6ce0no7ktiq.cloudfront.net"
  isLoading={awaitedPromise.type != 'resolved'}
  baseProps={awaitedPromise.value ?? previewProps}
  settings={{
    laminateLayerEnabled: materialSettings?.laminateSettings.enabled ?? true,
    patternScale: patternScale,
    lightweight: true,
    animationMode: StickerPreviewAnimationMode.None,
    effectLayerEnabled: !materialSettings?.type.includes('white'),
    holoIndex: 2,
    showFps: false,
    backgroundOption: BackgroundOption.Light,
  }}
  wiggle={true}
></StickerPreview>
